import { EmployeeSessionRequiredError } from '@lib/api';

import type { EmployeeSession } from '../api/session';

/**
 * Silent refresh of the employee-pool session (S8, CG2).
 *
 * The access token lives 15 minutes and the refresh token 8 hours, so a
 * viewer kept open during an incident would otherwise ask for a sign-in every
 * quarter hour. The rule, in both places it applies: refresh ONCE, retry the
 * original ONCE, and let a second failure through so the screen shows the
 * sign-in panel. Never a loop.
 *
 * Pure: the request, the refresh and the session read are passed in.
 */

/**
 * Run `attempt`; when it fails because the employee session is missing, refresh
 * once and run it once more. Any other failure, a refused refresh or a failed
 * refresh answers the ORIGINAL error, which is what the screen knows how to
 * render.
 */
export async function retryAfterRefresh<T>(
    attempt: () => Promise<T>,
    refresh: () => Promise<boolean>,
): Promise<T> {
    try {
        return await attempt();
    } catch (error) {
        if (!(error instanceof EmployeeSessionRequiredError)) throw error;
        const refreshed = await refresh().catch(() => false);
        if (!refreshed) throw error;
        return attempt();
    }
}

/**
 * One run at a time: callers arriving while a run is in flight share it. A
 * screen that fires four queries at once on an expired token asks Cognito
 * once, not four times.
 */
export function singleFlight<T>(run: () => Promise<T>): () => Promise<T> {
    let inFlight: Promise<T> | null = null;
    return () => {
        inFlight ??= run().finally(() => {
            inFlight = null;
        });
        return inFlight;
    };
}

const NO_SESSION: EmployeeSession = { permissions: [], expiresAt: null, refreshable: false };

/**
 * Read the session; when the server says it can be refreshed, refresh once and
 * answer the refreshed one. A refused refresh is "no session". A refresh that
 * FAILED is a failure, so the query retries rather than hiding the nav entry
 * for someone who is in fact signed in.
 */
export async function resolveSession(
    fetchSession: () => Promise<EmployeeSession>,
    refresh: () => Promise<EmployeeSession | null>,
): Promise<EmployeeSession> {
    const current = await fetchSession();
    if (!current.refreshable) return current;
    return (await refresh()) ?? NO_SESSION;
}
